/**
 * Phase 23 / Plan 23-05 / BDSEC-03 — Stepper-State-Helper.
 *
 * Mapped Mollie-Status auf vier visuelle Stepper-Steps:
 *   1. created    — Erstellt
 *   2. method     — Methode gewählt
 *   3. paid       — Bezahlt
 *   4. settled    — Settled (Webhook-acknowledged Endzustand)
 *
 * Mollie-Status-Vokabular: "open" / "pending" / "authorized" / "paid" /
 * "failed" / "expired" / "canceled" (Mollie wire value: 1 l).
 *
 * Quelle: `.claude/skills/sketch-findings-ko-X-main/references/mollie-status-and-actions.md`
 * (Variant D — Timeline-Stepper).
 */

export type StepperStepState = "pending" | "active" | "done" | "error" | "expired";

export interface StepperState {
    created: StepperStepState;
    method: StepperStepState;
    paid: StepperStepState;
    settled: StepperStepState;
}

export interface StepperAttemptShape {
    status: string;
    paidAt?: Date | string | null;
}

export type StepperFlowKind = "checkout" | "banktransfer";

function deriveBanktransferStepperState(
    attempt: StepperAttemptShape,
    bookingFullySettled?: boolean,
): StepperState {
    switch (attempt.status) {
        case "open":
        case "pending":
            return {created: "done", method: "active", paid: "pending", settled: "pending"};
        // WS-4: SEPA-Überweisung kennt keine authorized/Capture-Phase (im Gegensatz
        // zu Klarna/Karten). Ein defensiv möglicher "authorized"-Status fällt daher
        // bewusst in den neutralen default und erhält keine Capture-Semantik.
        case "paid":
            return {
                created: "done",
                method: "done",
                paid: "done",
                settled: bookingFullySettled ? "done" : "active",
            };
        // B-6967: Bei terminalem Nicht-Erfolg wurde die Überweisung nie geleistet —
        // der letzte erreichte Schritt ist "Warten auf Überweisung" (method). Diesen
        // als Fehler/Abbruch markieren statt als grünes Erfolgs-Häkchen ("done"),
        // sonst wirkt der Stepper erledigt, obwohl der Status-Tag "nicht aktiv" sagt.
        case "failed":
            return {created: "done", method: "error", paid: "pending", settled: "pending"};
        case "expired":
        case "canceled":
        // superseded: von einem neueren Versuch (z. B. durch die Korrektur) abgelöst — terminiert.
        case "superseded":
            return {created: "done", method: "expired", paid: "pending", settled: "pending"};
        default:
            return {created: "done", method: "pending", paid: "pending", settled: "pending"};
    }
}

/**
 * deriveStepperState — pure Funktion, mapped einen `MolliePaymentAttempt`-
 * status (plus optional `bookingFullySettled`) auf den 4-Schritt-Stepper-State.
 *
 * @param attempt   Mollie-Payment-Attempt (mind. `status`, optional `paidAt`).
 * @param bookingFullySettled
 *                  Wenn `true` UND attempt.status === "paid": settled-Step
 *                  wird auf "done" gesetzt (sonst "active"). Default: false.
 */
export function deriveStepperState(
    attempt: StepperAttemptShape,
    bookingFullySettled?: boolean,
    flowKind: StepperFlowKind = "checkout",
): StepperState {
    if (flowKind === "banktransfer") {
        return deriveBanktransferStepperState(attempt, bookingFullySettled);
    }

    switch (attempt.status) {
        case "open":
            // Link erstellt, aber Kunde hat noch keine Methode gewählt.
            return {created: "done", method: "pending", paid: "pending", settled: "pending"};
        case "pending":
            // Methode gewählt, aber Bezahlung noch nicht erfolgt (Banküberweisung).
            return {created: "done", method: "active", paid: "pending", settled: "pending"};
        case "authorized":
            // Methode gewählt + Reservation (Klarna), aber noch nicht erfasst.
            return {created: "done", method: "done", paid: "active", settled: "pending"};
        case "paid":
            // Erfolgreich bezahlt. Settled = done sobald booking-fully-settled.
            return {
                created: "done",
                method: "done",
                paid: "done",
                settled: bookingFullySettled ? "done" : "active",
            };
        case "failed":
            // Bezahlung fehlgeschlagen (Karte abgelehnt etc.).
            return {created: "done", method: "done", paid: "error", settled: "pending"};
        case "expired":
            // Link abgelaufen ohne Abschluss.
            return {created: "done", method: "expired", paid: "expired", settled: "pending"};
        case "canceled":
            // Mollie wire value: 1 l. UX-Logik analog expired.
            return {created: "done", method: "expired", paid: "expired", settled: "pending"};
        case "superseded":
            // Von einem neueren Versuch (z. B. durch die Korrektur) abgelöst — terminiert,
            // dieser Versuch ist nicht mehr der aktive. UX-Logik analog expired/canceled.
            return {created: "done", method: "expired", paid: "expired", settled: "pending"};
        default:
            // Defensive Fallback (unbekannter Status): nur created als done.
            return {created: "done", method: "pending", paid: "pending", settled: "pending"};
    }
}
